import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Table,
  Modal,
  message,
  Radio,
  DatePicker,
  Space,
  Tag,
} from 'antd';
import { PlusOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import axios from 'axios';
import type { ColumnsType } from 'antd/es/table';
import LatexText from '../components/LatexText';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.calloctech.com/api';

interface Question {
  id: string;
  question_text?: string;
  question?: string;
  subject: string;
  class: string;
  difficulty?: string;
  categories?: string;
  topic?: string;
  subtopic?: string;
  options?: any[];
}

export default function LiveTestCreate() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [questionTotal, setQuestionTotal] = useState(0);
  const [questionPage, setQuestionPage] = useState(1);
  const questionPageSize = 20;
  const [selectedQuestions, setSelectedQuestions] = useState<Question[]>([]);
  const [questionModalVisible, setQuestionModalVisible] = useState(false);
  const [searchFilters, setSearchFilters] = useState<any>({});
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    if (questionModalVisible) {
      loadQuestions(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchFilters, questionModalVisible]);

  const loadQuestions = async (page = questionPage) => {
    try {
      const response = await axios.get(`${API_URL}/admin/questions`, {
        params: { ...searchFilters, page, pageSize: questionPageSize },
      });
      const payload = response.data;
      setQuestions(Array.isArray(payload.data) ? payload.data : []);
      setQuestionTotal(payload.total ?? 0);
      setQuestionPage(page);
    } catch (error) {
      message.error('Failed to load questions');
    }
  };

  const filteredQuestions = questions.filter((question) => {
    if (!searchText.trim()) return true;
    const q = searchText.toLowerCase();
    const questionText = (question.question || question.question_text || '').toLowerCase();

    return (
      questionText.includes(q)
      || question.subject?.toLowerCase().includes(q)
      || question.class?.toLowerCase().includes(q)
      || question.categories?.toLowerCase().includes(q)
      || question.topic?.toLowerCase().includes(q)
      || question.subtopic?.toLowerCase().includes(q)
    );
  });

  const setFilterValue = (key: string, value: string | undefined) => {
    setQuestionPage(1);
    setSearchFilters((prev: Record<string, string>) => {
      const next = { ...prev };
      if (!value) {
        delete next[key];
      } else {
        next[key] = value;
      }
      return next;
    });
  };

  const handleCreateLiveTest = async (values: any) => {
    if (selectedQuestions.length === 0) {
      message.error('Please select at least one question');
      return;
    }

    try {
      setLoading(true);

      // Transform questions to match expected format
      const transformedQuestions = selectedQuestions.map((q: any) => ({
        id: q.id,
        question_text: q.question,
        options: [
          { id: 'a', option_text: q.option_a, is_correct: q.answer === 'A' },
          { id: 'b', option_text: q.option_b, is_correct: q.answer === 'B' },
          { id: 'c', option_text: q.option_c, is_correct: q.answer === 'C' },
          { id: 'd', option_text: q.option_d, is_correct: q.answer === 'D' },
        ],
        explanation: q.explanation,
      }));

      const liveTestData = {
        title: values.title,
        subject: values.subject,
        duration_minutes: values.duration_minutes,
        question_ids: selectedQuestions.map(q => q.id),
        questions: transformedQuestions,
        access_type: values.access_type,
        organization_id: values.access_type === 'organization' ? values.organization_id : null,
        organization_name: values.access_type === 'organization' ? values.organization_name : null,
        scheduled_at: values.scheduled_at.toDate(),
        created_by: 'teacher_id', // Get from auth context
        created_by_name: 'Teacher Name', // Get from auth context
      };

      // Create in PostgreSQL and Firestore
      await axios.post(`${API_URL}/live-tests`, liveTestData);

      message.success('Live test created successfully');
      form.resetFields();
      setSelectedQuestions([]);
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to create live test');
    } finally {
      setLoading(false);
    }
  };

  const questionColumns: ColumnsType<Question> = [
    {
      title: 'Question',
      dataIndex: 'question',
      key: 'question',
      width: '40%',
      render: (_text: string, record: Question) => (
        <LatexText text={record.question || record.question_text || ''} style={{ fontSize: 13, lineHeight: '1.6' }} />
      ),
    },
    {
      title: 'Subject',
      dataIndex: 'subject',
      key: 'subject',
      width: '15%',
    },
    {
      title: 'Class',
      dataIndex: 'class',
      key: 'class',
      width: '10%',
    },
    {
      title: 'Categories',
      dataIndex: 'categories',
      key: 'categories',
      width: '15%',
      render: (categories: string) => {
        if (!categories) return '-';
        return <Tag>{categories}</Tag>;
      },
    },
    {
      title: 'Action',
      key: 'action',
      width: '20%',
      render: (_: any, record: Question) => (
        <Button
          type="primary"
          size="small"
          icon={<PlusOutlined />}
          onClick={() => handleAddQuestion(record)}
        >
          Add
        </Button>
      ),
    },
  ];

  const selectedQuestionColumns: ColumnsType<Question> = [
    {
      title: '#',
      key: 'index',
      width: '10%',
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: 'Question',
      dataIndex: 'question',
      key: 'question',
      width: '60%',
      render: (_text: string, record: Question) => (
        <LatexText text={record.question || record.question_text || ''} style={{ fontSize: 13, lineHeight: '1.6' }} />
      ),
    },
    {
      title: 'Subject',
      dataIndex: 'subject',
      key: 'subject',
      width: '20%',
    },
    {
      title: 'Action',
      key: 'action',
      width: '10%',
      render: (_: any, record: Question) => (
        <Button
          danger
          size="small"
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveQuestion(record)}
        />
      ),
    },
  ];

  const handleAddQuestion = (question: Question) => {
    if (!selectedQuestions.find(q => q.id === question.id)) {
      setSelectedQuestions([...selectedQuestions, question]);
      message.success('Question added');
    } else {
      message.warning('Question already added');
    }
  };

  const handleRemoveQuestion = (question: Question) => {
    setSelectedQuestions(selectedQuestions.filter(q => q.id !== question.id));
    message.success('Question removed');
  };

  return (
    <div style={{ padding: '24px' }}>
      <Card title="Create Live Test" style={{ marginBottom: 24 }}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateLiveTest}
        >
          <Form.Item
            name="title"
            label="Test Title"
            rules={[{ required: true, message: 'Please enter test title' }]}
          >
            <Input placeholder="e.g., Physics Chapter 1 Live Test" />
          </Form.Item>

          <Form.Item
            name="subject"
            label="Subject"
            rules={[{ required: true, message: 'Please select subject' }]}
          >
            <Select placeholder="Select subject">
              <Select.Option value="Physics">Physics</Select.Option>
              <Select.Option value="Chemistry">Chemistry</Select.Option>
              <Select.Option value="Mathematics">Mathematics</Select.Option>
              <Select.Option value="Biology">Biology</Select.Option>
              <Select.Option value="English">English</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="duration_minutes"
            label="Duration (minutes)"
            rules={[{ required: true, message: 'Please enter duration' }]}
          >
            <Input type="number" placeholder="e.g., 30" />
          </Form.Item>

          <Form.Item
            name="access_type"
            label="Access Type"
            rules={[{ required: true, message: 'Please select access type' }]}
          >
            <Radio.Group>
              <Radio value="public">Public (All students can join)</Radio>
              <Radio value="organization">Organization Only</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.access_type !== currentValues.access_type
            }
          >
            {({ getFieldValue }) =>
              getFieldValue('access_type') === 'organization' ? (
                <>
                  <Form.Item
                    name="organization_id"
                    label="Organization ID"
                    rules={[{ required: true, message: 'Please enter organization ID' }]}
                  >
                    <Input placeholder="Organization UUID" />
                  </Form.Item>
                  <Form.Item
                    name="organization_name"
                    label="Organization Name"
                    rules={[{ required: true, message: 'Please enter organization name' }]}
                  >
                    <Input placeholder="Organization Name" />
                  </Form.Item>
                </>
              ) : null
            }
          </Form.Item>

          <Form.Item
            name="scheduled_at"
            label="Scheduled Date & Time"
            rules={[{ required: true, message: 'Please select date and time' }]}
          >
            <DatePicker showTime style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Card>

      <Card
        title={`Selected Questions (${selectedQuestions.length})`}
        extra={
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={() => setQuestionModalVisible(true)}
          >
            Add Questions
          </Button>
        }
        style={{ marginBottom: 24 }}
      >
        <Table
          columns={selectedQuestionColumns}
          dataSource={selectedQuestions}
          rowKey="id"
          pagination={false}
          locale={{ emptyText: 'No questions selected. Click "Add Questions" to select.' }}
        />
      </Card>

      <div style={{ textAlign: 'right' }}>
        <Space>
          <Button onClick={() => form.resetFields()}>Cancel</Button>
          <Button
            type="primary"
            loading={loading}
            onClick={() => form.submit()}
            disabled={selectedQuestions.length === 0}
          >
            Create Live Test
          </Button>
        </Space>
      </div>

      <Modal
        title="Select Questions"
        open={questionModalVisible}
        onCancel={() => setQuestionModalVisible(false)}
        width={1000}
        footer={null}
      >
        <div style={{ marginBottom: 16 }}>
          <Space>
            <Input
              placeholder="Search by question/topic/subtopic"
              allowClear
              style={{ width: 260 }}
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
            />
            <Select
              placeholder="Filter by subject"
              style={{ width: 150 }}
              allowClear
              onChange={(value) => setFilterValue('subject', value)}
            >
              <Select.Option value="Physics">Physics</Select.Option>
              <Select.Option value="Chemistry">Chemistry</Select.Option>
              <Select.Option value="Mathematics">Mathematics</Select.Option>
              <Select.Option value="Biology">Biology</Select.Option>
            </Select>
            <Select
              placeholder="Filter by class"
              style={{ width: 120 }}
              allowClear
              onChange={(value) => setFilterValue('class', value)}
            >
              <Select.Option value="9">Class 9</Select.Option>
              <Select.Option value="10">Class 10</Select.Option>
              <Select.Option value="11">Class 11</Select.Option>
              <Select.Option value="12">Class 12</Select.Option>
            </Select>
            <Select
              placeholder="Filter by topic"
              style={{ width: 180 }}
              allowClear
              onChange={(value) => setFilterValue('topic', value)}
              options={[...new Set(questions.map((item) => item.topic).filter(Boolean))].map((value) => ({
                label: value as string,
                value: value as string,
              }))}
            />
            <Select
              placeholder="Filter by subtopic"
              style={{ width: 180 }}
              allowClear
              onChange={(value) => setFilterValue('subtopic', value)}
              options={[...new Set(questions.map((item) => item.subtopic).filter(Boolean))].map((value) => ({
                label: value as string,
                value: value as string,
              }))}
            />
          </Space>
        </div>
        <Table
          columns={questionColumns}
          dataSource={filteredQuestions}
          rowKey="id"
          pagination={{
            current: questionPage,
            pageSize: questionPageSize,
            total: questionTotal,
            showTotal: (t) => `${t} questions`,
            onChange: (page) => loadQuestions(page),
          }}
        />
      </Modal>
    </div>
  );
}
